import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../service/auth/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  // A 401 from these is expected and handled by the caller — never trigger a refresh
  // or a redirect for them. `auth/me` is included because it runs at app startup for
  // anonymous visitors too, and must not bounce them off the page they asked for.
  private isAuthEndpoint(url: string): boolean {
    return url.includes('auth/login') ||
           url.includes('auth/logout') ||
           url.includes('auth/refresh') ||
           url.includes('auth/me');
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const clonedRequest = request.clone({
      withCredentials: true,
      setHeaders: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.isAuthEndpoint(request.url)) {
          return this.handleTokenRefresh(clonedRequest, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  private handleTokenRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshDone$.pipe(
        filter(done => done),
        take(1),
        switchMap(() => next.handle(request))
      );
    }

    this.isRefreshing = true;
    this.refreshDone$.next(false);

    return this.authService.refreshToken().pipe(
      switchMap(() => {
        this.isRefreshing = false;
        this.refreshDone$.next(true);
        return next.handle(request);
      }),
      catchError(() => {
        this.isRefreshing = false;
        // Session expirée côté serveur : même exigence qu'une déconnexion volontaire —
        // repartir d'une page neuve, sinon les magasins gardent les données du compte.
        this.authService.terminerSession();
        return throwError(() => originalError);
      })
    );
  }
}
